/**
 * Wall handstand hold. **Still uncalibrated**, but no longer blind: one take
 * exists and it answered the question this drill had that the others did not.
 *
 * MediaPipe's pose model is trained overwhelmingly on people the right way up,
 * so until there was footage, nothing here was known to measure anything at all.
 * There is now (`fixtures/wall_handstand/`), and the model tracks an inverted
 * body cleanly: 0.97-0.98 visibility along the whole chain through a 46-second
 * hold, the ankle above the shoulder in 3091 of 3098 inverted frames, and
 * `trunkTilt` reaching 172-180 as the drill assumes.
 *
 * What one take cannot do is calibrate seven thresholds. It calibrated two,
 * because it contained a fault performed on purpose: the head criterion below,
 * which that fault was, and the knee, which the take showed failing a straight
 * leg. The rest are still a diver's line written down as angles, the drill still
 * ships `calibrated: false`, and `docs/wall-handstand.md` records what each
 * number now rests on and what is still unanswered.
 *
 * What the drill is, as asked for: a hold against a wall, filmed side-on, in a
 * diver's line — hands, shoulders, hips and feet stacked, legs and arms
 * straight. Either way round, chest to the wall or back to it.
 *
 * **Two questions per frame, kept apart.** Being upside down is what opens and
 * closes the hold; being in line is what the athlete hears about. They are
 * deliberately not the same test, because a hold that could only exist while the
 * line was good would make every criterion below pass by construction — see
 * `exercises/hold.js` and the V-sit's note in `docs/exercises.md`.
 *
 * **The line is the hip and the knee judged apart, not one angle through the
 * whole body.** Shoulder-hip-ankle was written first and is worse: bending a
 * knee moves the ankle, so one fault failed two criteria and the athlete was
 * told to stack his hips about his knees. Shoulder-hip-knee isolates the fold at
 * the hip, the knee angle isolates the leg, and a body straight at both IS the
 * line — there is nothing left for a third angle to catch. What the projection
 * cannot tell is a pike from an arch: both leave the line on the same axis, so
 * the cue says "Stack your hips" rather than naming a direction it did not
 * measure.
 *
 * **The wall is found without being seen, because the feet are on it.** There is
 * no wall in the landmarks, but a wall handstand rests its feet against one, so
 * the horizontal gap between the wrist and the ankle IS how far the hands sit
 * out from the wall. That is the distance the athlete wants under 30cm, and the
 * same measurement read for its sign says which way round he is: the body leans
 * the way the wall is, and which way he faces (`facingOffset`) says whether that
 * side is his chest or his back. The distance is judged; the side is only a
 * label on the row, because a wrong guess at it costs a word and a straight line
 * is a straight line either way round.
 *
 * Two things to hold against that number. It is a projection, so it is the gap
 * along the camera's x rather than along the floor, which is the same thing only
 * while the camera is square to the wall. And `worldLandmarks` are metres by
 * estimate rather than by measurement, so 30cm here is 30cm as MediaPipe reckons
 * it. Both are reasons the row prints its figure rather than only a verdict, and
 * reasons to put a tape next to it on the first take.
 *
 * **The distance makes no sound.** It is set before the athlete goes up and
 * cannot be changed while he is inverted, so announcing it mid-hold would be
 * telling him about something he can only act on once he comes down. It is on
 * the readout and on the ticket, which is where a setup fault belongs.
 *
 * **The ankle angle does not track the foot, and the row no longer claims to.**
 * The athlete asked for pointed feet, so this started as a criterion — the
 * knee-ankle-toe angle, 180 being the foot continuing the shin, rather than the
 * squat jump's heel height, which means nothing with no floor under the foot. It
 * was never wired to the sound, because this app had already had one criterion
 * on exactly this joint turn out to be reading the camera rather than the body
 * (the jump's "Push-off"). A take then retired it here too, and by the cleanest
 * test available: the athlete flexed his feet deliberately and corrected them
 * **within one hold**, which removes between-take variability altogether. The
 * ankle angle does not step. Its one-second medians wander between 149 and 168
 * with no regime, and the best single split point anywhere in a 34 second hold
 * separates the two halves by 3.2 degrees — at the noise floor, and in the
 * direction that says correcting the feet made them worse. Identical on the raw
 * landmarks, with the foot landmarks reporting 0.93-0.97 visibility throughout.
 *
 * So the row is `informational` with `pass` permanently null, labelled `Ankle`
 * for the angle it computes and noted as not being toe point, and it has no cue.
 * That is the squat jump's resolution applied to the same joint a second time.
 * Two drills, one upright and one inverted, on the same phone at a similar
 * distance, both say MediaPipe's foot landmarks do not follow the foot here; see
 * AGENTS.md. Pointed toes are not currently measurable in this app, and a row
 * that said otherwise would be the one failure mode a coaching app cannot
 * afford — telling an athlete he is wrong about something nobody measured.
 *
 * **The head is judged on which side of the arm it is, not how far.** The
 * athlete asked for the ears between the arms, and `earOffArmLine` is an angle
 * at the shoulder between the arm and the ear whose SIGN is the whole signal.
 * The magnitude separates nothing: on the reference take a good hold reads 8
 * degrees off the arm line at one moment and 28 at another, and a deliberately
 * broken one reads 7. The sign flips exactly where the posture breaks — clean
 * -8 to -28, broken +10 to +47 — which is why this is the one criterion on the
 * drill with a fault take behind it and the one that is wired to the sound.
 *
 * **Positive is the ear BEHIND the arms**, away from the way the athlete faces,
 * which is the direction the fault actually went: on the reference take the
 * broken stretches put the ear 3.6-7.4cm to the back of where a clean hold put
 * it, and pulled it 2.6cm closer to the shoulder with it. That is a head thrown
 * back, which is what letting it out of the line looks like. It was worth
 * measuring rather than assuming, because the obvious guess — the head dropping
 * forward towards the chest — is the other sign entirely.
 *
 * Its sign is only meaningful once the facing is applied, and the facing is a
 * property of the take rather than of the frame — the same reason `aheadOfKnee`
 * takes it as an argument. Here it has to be read per frame anyway, because the
 * sound is per frame, so instead the facing gets a floor: below
 * `handstandFacingMin` of nose-to-ear offset the head is reported unjudged
 * rather than guessed, since a facing read backwards would invert the criterion
 * and tell an athlete he is wrong while he holds a good handstand. On the
 * reference take that offset never fell below 4.7cm and never changed sign
 * across 2777 frames, which is what makes reading it per frame defensible.
 *
 * **`headPitchFromTrunk` is still not used.** It is measured against
 * `neckOffset`, pinned from a still of this athlete standing up, and upside down
 * that offset is not known to mean anything. The ear against the arm needs no
 * constant.
 */
import { HoldCounter } from "./hold";

function median(values) {
  const sorted = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (sorted.length === 0) {
    return null;
  }
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * Whether this frame is part of a hold: the body is inverted.
 *
 * `trunkTilt` is the trunk's lean off world up, signed, so standing is near 0
 * and upside down is near ±180 whichever way the athlete faces. The magnitude is
 * what matters, and the threshold sits well off vertical so that the kick-up and
 * the way down are not part of the hold.
 */
export function inverted(sample, thresholds) {
  return Math.abs(sample.trunkTilt) >= thresholds.handstandInvertMin;
}

/**
 * How far the ear sits past the line of the arm, in degrees, signed so that
 * positive is BEHIND the arms — away from the way the athlete faces — which is
 * the fault. Null when the facing is too weak to call, because the facing is
 * what gives the figure its sign.
 */
export function headOutOfLine(sample, side, thresholds) {
  const raw = sample.earOffArmLineRaw[side];
  const facing = sample.facingOffset[side];
  if (
    raw == null ||
    !Number.isFinite(raw) ||
    !Number.isFinite(facing) ||
    Math.abs(facing) < thresholds.handstandFacingMin
  ) {
    return null;
  }
  return Math.sign(facing) * raw;
}

/**
 * Whether the posture is right on this frame: all five readings at once.
 *
 * All five, because a line is the whole body and a handstand with straight legs
 * and a shut shoulder is not one. This is the test the athlete hears, and the
 * criteria below report each reading separately so the ticket can say which one
 * it was.
 *
 * The head is in here and the toes and the hand distance are not, which is not
 * an ordering of how much they matter. The head is the one reading on this drill
 * that a take has shown moving with the body; the toes are the one this app has
 * already had read the camera instead, on that same joint; and the distance to
 * the wall cannot be changed while the athlete is upside down.
 *
 * A head that cannot be judged — the facing too weak to give it a sign — does
 * not fail the line. An unjudged reading is not a fault, and the other four
 * still describe a line without it.
 */
export function inLine(sample, side, thresholds) {
  const line = sample.hip[side];
  const knee = sample.knee[side];
  const elbow = sample.elbow[side];
  const shoulder = sample.shoulder[side];
  const head = headOutOfLine(sample, side, thresholds);
  return (
    Number.isFinite(line) &&
    Number.isFinite(knee) &&
    Number.isFinite(elbow) &&
    Number.isFinite(shoulder) &&
    line >= thresholds.handstandLineMin &&
    knee >= thresholds.handstandKneeMin &&
    elbow >= thresholds.handstandElbowMin &&
    shoulder >= thresholds.handstandShoulderMin &&
    (head == null || inHeadBand(head, thresholds))
  );
}

/**
 * Whether the head sits inside the band, which has two edges because a head is
 * out of line in two directions.
 *
 * The first version had one: positive was the ear thrown back out behind the
 * arm line, which was the fault the second take performed, and anything
 * negative passed. So a head held far forward — ears nowhere near the arms,
 * eyes off the hands — scored a clean hold, which is what the athlete reported
 * after watching it happen. The third take broke it both ways inside one hold
 * and the two faults sit either side of a clean band: see params.js.
 */
export function inHeadBand(head, thresholds) {
  return (
    head <= thresholds.handstandHeadMax && head >= thresholds.handstandHeadMin
  );
}

/**
 * Seconds of the hold spent in line, summed over the frames.
 *
 * The same sum the counter keeps live for the bar, recomputed here off the same
 * predicate so the figure on the ticket and the figure the athlete watched are
 * one number. Each interval belongs to the reading the frame before it gave.
 */
function secondsInLine(samples, side, thresholds) {
  let ms = 0;
  for (let i = 1; i < samples.length; i += 1) {
    if (inLine(samples[i - 1], side, thresholds)) {
      ms += samples[i].t - samples[i - 1].t;
    }
  }
  return ms / 1000;
}

/**
 * Chest to the wall or back to it, from the lean and the facing. Null when the
 * body was too close to plumb to call it, which is the honest answer rather
 * than a coin toss.
 */
function wallSide(samples, side, thresholds) {
  const lean = median(samples.map((s) => s.wallLean[side]));
  const facing = median(samples.map((s) => s.facingOffset[side]));
  if (lean == null) {
    return { lean: null, side: null };
  }
  if (
    facing == null ||
    facing === 0 ||
    Math.abs(lean) < thresholds.handstandLeanMin
  ) {
    return { lean, side: null };
  }
  // The feet lean towards the wall. If that is the way the athlete's front
  // points, the chest is against it.
  return {
    lean,
    side: Math.sign(lean) === Math.sign(facing) ? "front" : "back",
  };
}

export const wallHandstand = {
  id: "wall_handstand",
  name: "Wall handstand",
  blurb: "Hold the line upside down, either way round",
  /**
   * Side-on. Every angle here is sagittal — the line through the body, the
   * knee, the elbow, the shoulder — and from the front they all live along the
   * axis the geometry drops. It is also the only view from which the lean that
   * finds the wall is visible at all.
   */
  view: "side",
  /**
   * Not calibrated, and further from it than the V-sit: the V-sit's numbers are
   * anatomy applied to a movement the model tracks. These are anatomy applied to
   * a body the model has never been checked against. See the module note.
   */
  calibrated: false,
  timed: true,
  thresholdKeys: [
    "handstandInvertMin",
    "handstandLineMin",
    "handstandKneeMin",
    "handstandElbowMin",
    "handstandShoulderMin",
    "handstandMinHoldMs",
    "handstandSettleMs",
    "handstandLeanMin",
    "handstandWallMax",
    "handstandHeadMax",
    "handstandHeadMin",
    "handstandFacingMin",
  ],
  cues: {
    hs_line: "Stack your hips",
    hs_legs: "Straighten your legs",
    hs_elbows: "Lock your elbows",
    hs_shoulders: "Push the floor away",
    // No cue for the ankle: it reports and never judges, so nothing it says
    // could be acted on. Nothing claims anything about the toes.
    hs_head: "Ears between your arms",
    hs_wall: "Hands closer to the wall",
  },
  /** What the athlete hears mid-hold, as the state changes. */
  stateCues: { good: "In line", bad: "Find the line" },
  needsFootScale: false,
  createCounter: (thresholds) =>
    new HoldCounter({
      holding: (sample) => inverted(sample, thresholds),
      inLine: (sample, side) => inLine(sample, side, thresholds),
      // The longest visibility gate in the app, because the measurement is a
      // line from the hand to the foot and a guess anywhere along it is a guess
      // about all of it.
      visibility: (sample, side) => sample.handstandVisibility[side],
      minVisibility: thresholds.minVisibility,
      maxGapMs: thresholds.maxGapMs,
      minHoldMs: thresholds.handstandMinHoldMs,
      settleMs: thresholds.handstandSettleMs,
    }),
  /**
   * One hold's samples in, criterion rows out.
   *
   * Each joint reports its TYPICAL reading over the hold rather than its best or
   * its worst. A hold is a sustained thing: the best frame says the shape
   * existed for a moment, the worst says the athlete blinked, and the middle is
   * the only one of the three that describes what was held.
   */
  scoreRep: (samples, { side, thresholds }) => {
    if (samples.length === 0) {
      return [];
    }
    const seen =
      median(samples.map((s) => s.handstandVisibility[side])) >=
      thresholds.minVisibility;
    const typical = (read) => (seen ? median(samples.map(read)) : null);
    const line = typical((s) => s.hip[side]);
    const knee = typical((s) => s.knee[side]);
    const elbow = typical((s) => s.elbow[side]);
    const shoulder = typical((s) => s.shoulder[side]);
    const toes = typical((s) => s.ankle[side]);
    // Read per frame, each with its own facing, and then taken over the hold
    // like every other reading here. Frames whose facing was too weak drop out
    // of the median rather than counting as good, and a hold with none left
    // reports unjudged.
    const head = seen
      ? median(samples.map((s) => headOutOfLine(s, side, thresholds)))
      : null;
    const held = secondsInLine(samples, side, thresholds);
    const wall = seen
      ? wallSide(samples, side, thresholds)
      : { lean: null, side: null };

    return [
      {
        id: "hs_line",
        label: "Body line",
        value: line,
        unit: "°",
        precision: 0,
        pass: line == null ? null : line >= thresholds.handstandLineMin,
      },
      {
        id: "hs_legs",
        label: "Legs straight",
        value: knee,
        unit: "°",
        precision: 0,
        pass: knee == null ? null : knee >= thresholds.handstandKneeMin,
      },
      {
        id: "hs_elbows",
        label: "Elbows locked",
        value: elbow,
        unit: "°",
        precision: 0,
        pass: elbow == null ? null : elbow >= thresholds.handstandElbowMin,
      },
      {
        id: "hs_shoulders",
        label: "Shoulders open",
        value: shoulder,
        unit: "°",
        precision: 0,
        pass:
          shoulder == null ? null : shoulder >= thresholds.handstandShoulderMin,
      },
      {
        // The ankle angle, which is a real angle and not a reading of what the
        // athlete does with his foot: see the module note. Reports and never
        // judges, like the squat jump's row on the same joint.
        id: "hs_toes",
        label: "Ankle",
        value: toes,
        unit: "°",
        precision: 0,
        informational: true,
        note: "not toe point",
        pass: null,
      },
      {
        // The criterion with performed faults behind it in both directions,
        // which is why it is also the one that reaches the sound. Positive is
        // the ear out behind the arms, which is the head thrown back; negative
        // is the head coming forward off the line, and far enough that way is
        // just as wrong. The band between them is where the ears belong. The
        // figure is printed signed for that reason — a magnitude would throw
        // away the only part of it that means anything, and would fold the two
        // faults onto each other.
        id: "hs_head",
        label: "Ears in line",
        value: head,
        unit: "°",
        precision: 0,
        pass: head == null ? null : inHeadBand(head, thresholds),
      },
      {
        // The headline figure, and the one thing on this drill that is a
        // measurement rather than a judgement: a clock. It adds up over the set
        // the way the jump's air time does, so the bar carries a running total
        // and the ticket prints the set's seconds with the best hold beside it.
        id: "hs_held",
        label: "In line",
        value: held,
        unit: "s",
        precision: 1,
        informational: true,
        aggregate: "sum",
        bonusStep: 5,
        note: "seconds",
        pass: null,
      },
      {
        // How far the hands sit out from the wall, which is the gap between the
        // wrist and the ankle because the feet are against it. Judged: the
        // athlete asked for under 30cm, and unlike the line it is set before he
        // goes up rather than held.
        id: "hs_wall",
        label: "Hands to wall",
        value: wall.lean == null ? null : Math.abs(wall.lean) * 100,
        unit: "cm",
        precision: 0,
        // Which way round he is rides along as the note rather than as a row of
        // its own: it is the same measurement read for its sign, and it is a
        // label rather than a judgement.
        note:
          wall.side == null
            ? "not sure"
            : wall.side === "front"
              ? "chest"
              : "back",
        pass:
          wall.lean == null
            ? null
            : Math.abs(wall.lean) <= thresholds.handstandWallMax,
      },
    ];
  },
  metricIds: [],
};
